import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { kMatterDialogGenericErrorMessage } from '@/matter/constants'
import { deleteMatter } from '@/matter/fetch/delete-matter'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { ModalFooterWithActionsType } from '@/components/modal/constants'
import { useEffect, useState } from 'react'

/**
 * Delete Matter Dialog
 */
export default function DeleteMatterDialog() {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Local state
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Redux state
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)
  const dialogState = useAppSelector((state: RootState) => state.mattersState.dialog)
  const matter = useAppSelector((state: RootState) => state.mattersState.matters[dialogState.matterId ?? ''])

  const isDialogOpen = dialogState.dialogType === MatterDialogType.Delete

  // Handles success actions after a matter is deleted
  const onSuccess = (deletedMatterId: string) => {
    const matterName = matter?.name

    setIsSubmitting(false)
    dispatch(MattersActions.closeDialog())
    dispatch(
      openGlobalToast({
        type: GlobalToastType.SUCCESS,
        message: `Deleted matter${matterName ? `: ${matterName}` : ''}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 3,
      })
    )

    // If the deleted matter was active, clear the active matter
    if (activeMatterId === deletedMatterId) {
      dispatch(MattersActions.setActiveMatterId({ matterId: null }))
    }

    // Remove matter from the store
    dispatch(MattersActions.removeMatter({ matterId: deletedMatterId }))
  }

  /**
   * Handles submission for deleting a matter.
   *
   * - Sends a delete request to the server
   * - On success: tracks analytics, updates Redux state, shows a toast, and closes the dialog
   * - On error: sets an error for display to the user
   */
  const handleDelete = async () => {
    setSubmitError(null)
    setIsSubmitting(true)

    const deleteMatterId = dialogState.matterId
    if (!deleteMatterId) {
      throw new Error('Deleting a matter requires a matter ID, but none was provided.')
    }

    try {
      await deleteMatter(deleteMatterId)

      trackEvent(AnalyticsEvent.MatterDeleted)

      // Perform success actions after a brief delay for a smoother visual transition
      setTimeout(() => onSuccess(deleteMatterId), 600)
    } catch {
      setSubmitError(kMatterDialogGenericErrorMessage)
      setIsSubmitting(false)
    }
  }

  /*
   * Reset the dialog local state when the dialog is opened (fresh session)
   */
  useEffect(() => {
    if (isDialogOpen) {
      setSubmitError(null)
      setIsSubmitting(false)
    }
  }, [isDialogOpen])

  return (
    <Modal open={isDialogOpen} onOpenChange={() => dispatch(MattersActions.closeDialog())}>
      <ModalContent
        title="Delete Matter?"
        subtitle="All documents and history associated with this matter will be deleted. This action cannot be undone."
        width="md"
        height="max-md"
        footer={
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            secondaryAction={{ label: 'Cancel', onClick: () => dispatch(MattersActions.closeDialog()) }}
            primaryAction={{ label: 'Delete', onClick: handleDelete, loading: isSubmitting }}
            inlineMessage={submitError ? { variant: 'error', children: submitError } : undefined}
          />
        }
      />
    </Modal>
  )
}
